<template lang="pug">
div
  //- Header row: label + active/inactive switch.
  .d-flex.gap-2.align-items-center.justify-content-between.mb-2
    label.m-0 {{ label }}
    .d-flex.align-items-center
      span.text-muted.mr-2 {{ active ? "Active" : "No change" }}
      b-form-checkbox(switch, :checked="active", @change="toggleActive")

  b-form-radio-group.impratio-toggle.mb-2(
    v-if="active",
    :checked="mode",
    buttons,
    button-variant="light",
    size="sm",
    @change="setMode"
  )
    b-form-radio(:value="'impression_ratio'", button-class="impratio-btn", v-b-tooltip.hover.top="'Show in Impression Ratio format'") Impressions
    b-form-radio(:value="'ctr'", button-class="impratio-btn", v-b-tooltip.hover.top="'Show in CTR Goal format'") CTR

  //- Swap the inner editor with a short fade transition.
  transition(name="fade", mode="out-in")
    ctr-goal(
      v-if="active && mode === 'ctr'",
      :key="'ctr'",
      :value="value",
      @input="$emit('input', $event)"
    )
    impression-ratio(
      v-else-if="active && mode === 'impression_ratio'",
      :key="'impression_ratio'",
      :value="value",
      @input="$emit('input', $event)"
    )
    .form-group(v-else, :key="'none'")
      input.form-control(
        readonly,
        type="text",
        value="",
      )
      p.form-text.text-muted No change (as sent by publisher) 

  p.form-text.text-warning(v-if="showMissingClickCapWarning")
    i.la.la-info-circle.mr-1(
      v-b-tooltip.hover.top="'Set a Daily Click Cap to calculate and save a Daily Impression Cap'"
    )
    | Impression cap is open until a Daily Click Cap is set
  
</template>

<style lang="scss" scoped>
// Compact segmented buttons aligned with project UI.
.impratio-toggle {
  .btn {
    font-size: 11px;
    padding: 4px 10px;
    &.active {
      background-color: #ffb047;
      color: #fff;
    }
  }

  .impratio-btn {
    transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
  }
  .impratio-btn:hover {
    border-color: #c6c6c6;
  }
}
</style>

<script>
// Central helper for resolving default format from user settings.
import {
  calculateDerivedImpressionCap,
  isImpressionRateActive,
  resolveImpressionCapOnRateChange,
  shouldShowMissingClickCapWarning,
} from "@/utils/impression-rate-ctr-goal";
import { getImpratioFormat } from "@/utils/impratio-format";

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Impratio",
  props: {
    // impression_rate model value (-1 means "no change").
    value: {
      type: [String, Number],
    },
    // Optional override for the primary format (mainly for explicit callers).
    primaryFormat: {
      type: String,
      default: null,
    },
    clickCap: {
      type: [String, Number],
      default: null,
    },
    // Current daily_impression_cap of the entity. Needed to remember the operator's own cap
    // while the rate derives it, and to put it back when the rate stops deriving it.
    impressionCap: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    // Local UI mode (doesn't mutate props).
    return {
      mode: this.getDefaultMode(),
      // Impression cap as it was before the rate started deriving it (null = nothing to restore).
      manualImpressionCap: null,
    };
  },
  methods: {
    // Read primary format from prop or store
    resolvePrimaryFormat() {
      if (this.primaryFormat) {
        return this.primaryFormat;
      }
      const user = this.$store && this.$store.state ? this.$store.state.user : null;
      return getImpratioFormat(user);
    },
    // Pick the initial mode based on the current value and primary format.
    getDefaultMode() {
      const primary = this.resolvePrimaryFormat();
      if (!isImpressionRateActive(this.value)) {
        return primary;
      }
      if (+this.value === 0 || +this.value === 100) {
        return "impression_ratio";
      }
      if (primary === "impression_ratio") {
        return "impression_ratio";
      }
      return "ctr";
    },
    // Toggle the "no change" state and jump to the primary format when enabling.
    toggleActive(nextActive) {
      if (!nextActive) {
        this.$emit("input", -1);
        this.mode = this.resolvePrimaryFormat();
        return;
      }

      this.mode = this.resolvePrimaryFormat();
      if (!isImpressionRateActive(this.value)) {
        this.$emit("input", 99.9);
      }
    },
    // Switch between CTR / Impressions modes while keeping the stored value valid.
    setMode(nextMode) {
      this.mode = nextMode;
      if (!isImpressionRateActive(this.value)) {
        this.$emit("input", 99.9);
      }
    },
    emitDerivedImpressionCap() {
      if (!this.autoImpressionCapCalculation) {
        return;
      }
      if (this.clickCap === null || this.clickCap === undefined) {
        return;
      }

      const impressionCap = calculateDerivedImpressionCap(
        this.clickCap,
        this.value,
      );
      if (impressionCap !== null) {
        this.$emit("update-impression-cap", impressionCap);
      }
    },
    // Rate changed: derive the cap, or restore the manual one when the rate stops deriving it.
    syncImpressionCapOnRateChange(nextRate, previousRate) {
      if (!this.autoImpressionCapCalculation) {
        return;
      }
      if (this.clickCap === null || this.clickCap === undefined) {
        return;
      }

      const { impressionCap, manualImpressionCap } = resolveImpressionCapOnRateChange({
        previousRate,
        nextRate,
        clickCap: this.clickCap,
        currentImpressionCap: this.impressionCap,
        manualImpressionCap: this.manualImpressionCap,
      });
      this.manualImpressionCap = manualImpressionCap;
      if (impressionCap !== null) {
        this.$emit("update-impression-cap", impressionCap);
      }
    },
  },
  computed: {
    autoImpressionCapCalculation() {
      return this.$store.state.user.config.impressionCapAutoCalculation;
    },
    // Whether the field is enabled (anything except -1).
    active() {
      return isImpressionRateActive(this.value);
    },
    // Label follows the effective format even when the field is currently inactive.
    label() {
      const mode = this.active ? this.mode : this.resolvePrimaryFormat();
      if (mode === "impression_ratio") {
        return "Impression Ratio";
      }
      return "CTR Goal %";
    },
    showMissingClickCapWarning() {
      return this.autoImpressionCapCalculation
        && shouldShowMissingClickCapWarning(this.clickCap, this.value);
    },
  },
  watch: {
    // Keep UI mode in sync when the parent value flips to/from -1.
    value: {
      immediate: true,
      handler(nextRate, previousRate) {
        this.syncImpressionCapOnRateChange(nextRate, previousRate);
        if (!isImpressionRateActive(this.value)) {
          this.mode = this.resolvePrimaryFormat();
        }
      },
    },
    clickCap() {
      this.emitDerivedImpressionCap();
    },
    // If caller changes the primary format, re-align the mode accordingly.
    primaryFormat() {
      this.mode = this.getDefaultMode();
    },
  },
};
</script>
